import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ParameterRow from './ParameterRow';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
}));

/**
 * Data Catalog Table Component
 * Renders the complete parameter table with header and body
 *
 * @param {Array} parameters - Sorted parameters to display
 * @param {string} nameView - Name view mode ('charite', 'omop', 'both')
 * @param {Array} expandedRows - Array of expanded parameter IDs
 * @param {Function} onRowClick - Handler for row click
 * @param {Function} onFavoriteToggle - Handler for favorite toggle
 * @param {Function} handleOpenListDialog - Handler for list dialog
 * @param {string} orderBy - Column to sort by
 * @param {string} order - Sort order ('asc' or 'desc')
 * @param {Function} handleRequestSort - Handler for sort request
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} getParameterListStatus - Get list status for parameter
 * @param {Function} isParameterInAnyList - Check if parameter is in any list
 * @param {Function} handleRemoveFromList - Remove parameter from list
 * @param {Function} handleAddToList - Add parameter to list
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} getPrimaryDescription - Get primary description
 * @param {Object} subtlePulse - Animation for deletion requests
 */
const DataCatalogTable = ({
  parameters,
  nameView,
  expandedRows,
  onRowClick,
  onFavoriteToggle,
  handleOpenListDialog,
  orderBy,
  order,
  handleRequestSort,
  activeWorkingList,
  parameterLists,
  getParameterListStatus,
  isParameterInAnyList,
  handleRemoveFromList,
  handleAddToList,
  allTagsWithStats,
  getIconComponent,
  getPrimaryDescription,
  subtlePulse,
}) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {/* Single List Column */}
            {activeWorkingList.length === 1 && (
              <StyledTableCell width={45} align="center">
                <TableSortLabel
                  active={orderBy === 'lists'}
                  direction={orderBy === 'lists' ? order : 'asc'}
                  onClick={() => handleRequestSort('lists')}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '& .MuiTableSortLabel-icon': {
                      position: 'absolute',
                      right: 0
                    }
                  }}
                >
                  A
                </TableSortLabel>
              </StyledTableCell>
            )}

            {/* List Comparison Columns */}
            {activeWorkingList.length === 2 && (
              <>
                <StyledTableCell width={45} align="center">
                  <TableSortLabel
                    active={orderBy === 'listA'}
                    direction={orderBy === 'listA' ? order : 'asc'}
                    onClick={() => handleRequestSort('listA')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiTableSortLabel-icon': {
                        position: 'absolute',
                        right: 0
                      }
                    }}
                  >
                    A
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell width={45} align="center">
                  <TableSortLabel
                    active={orderBy === 'listB'}
                    direction={orderBy === 'listB' ? order : 'asc'}
                    onClick={() => handleRequestSort('listB')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiTableSortLabel-icon': {
                        position: 'absolute',
                        right: 0
                      }
                    }}
                  >
                    B
                  </TableSortLabel>
                </StyledTableCell>
              </>
            )}

            {/* Standard Columns */}
            <StyledTableCell width={50}></StyledTableCell>
            <StyledTableCell>Parameter</StyledTableCell>
            <StyledTableCell>Tags</StyledTableCell>
            <StyledTableCell width={200}>Häufigkeit</StyledTableCell>
            {nameView !== 'charite' && <StyledTableCell>OMOP Status</StyledTableCell>}
            <StyledTableCell>Sensitivität</StyledTableCell>
            <StyledTableCell width={150} align="center">Aktionen</StyledTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {parameters.map((param) => (
            <ParameterRow
              key={param.id}
              param={param}
              nameView={nameView}
              isExpanded={expandedRows.includes(param.id)}
              onRowClick={onRowClick}
              onFavoriteToggle={onFavoriteToggle}
              activeWorkingList={activeWorkingList}
              parameterLists={parameterLists}
              getParameterListStatus={getParameterListStatus}
              isParameterInAnyList={isParameterInAnyList}
              handleRemoveFromList={handleRemoveFromList}
              handleAddToList={handleAddToList}
              handleOpenListDialog={handleOpenListDialog}
              allTagsWithStats={allTagsWithStats}
              getIconComponent={getIconComponent}
              getPrimaryDescription={getPrimaryDescription}
              subtlePulse={subtlePulse}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataCatalogTable;
